import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CameraIcon, CheckIcon, HomeIcon, LocateFixedIcon, MapPinIcon, QrCodeIcon, ScanLineIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { AddressAutocomplete } from '../demandes/AddressAutocomplete';
import { detectedPosition, profileAddress } from '../../data/demandes';
const ease = [0.23, 1, 0.32, 1] as const;
type Mode = 'choix' | 'scan' | 'scan-resultat' | 'signalement' | 'transmis';
type LocationId = 'position' | 'residence' | 'autre';

// Mêmes choix que le formulaire « Nouvelle demande » : géolocalisation simulée en premier.
const locationOptions: {
  id: LocationId;
  label: string;
  detail?: string;
  Icon: typeof MapPinIcon;
}[] = [{
  id: 'position',
  label: 'Ma position actuelle',
  detail: detectedPosition,
  Icon: LocateFixedIcon
}, {
  id: 'residence',
  label: 'Résidence',
  detail: profileAddress,
  Icon: HomeIcon
}, {
  id: 'autre',
  label: 'Autre endroit',
  Icon: MapPinIcon
}];
interface FoundPetDialogProps {
  open: boolean;
  onClose: () => void;
}
export function FoundPetDialog({
  open,
  onClose
}: FoundPetDialogProps) {
  const [mode, setMode] = useState<Mode>('choix');
  const [photo, setPhoto] = useState(false);
  const [place, setPlace] = useState('');
  const [location, setLocation] = useState<LocationId>('position');
  const close = () => {
    onClose();
    window.setTimeout(() => {
      setMode('choix');
      setPhoto(false);
      setPlace('');
      setLocation('position');
    }, 250);
  };
  return <AnimatePresence data-id="element-85" data-mp="3t63t06sye4d8">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-86" data-mp="3t5u46mjna394">
          <button type="button" aria-label="Fermer" onClick={close} className="absolute inset-0 bg-ville-navy/40" data-id="element-87" data-mp="20kj99aicf6d" />
          <motion.div role="dialog" aria-modal="true" aria-label="Animal trouvé" initial={{
        y: 20,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 12,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl" data-id="element-88" data-mp="2vt22cgfpevs6">
            <button type="button" onClick={close} aria-label="Fermer" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-89" data-mp="3jhle55xypqp0">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-90" data-mp="1y4a6tckn47wu" />
            </button>

            <h2 className="pr-10 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-91" data-mp="1y6cw1udyxx0i">Animal trouvé</h2>

            <AnimatePresence mode="wait" data-id="element-92" data-mp="1qmkfnfhcouc7">
              <motion.div key={mode} initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} transition={{
            duration: 0.22,
            ease
          }} className="mt-5" data-id="element-93" data-mp="1ltrbottphq16">
                {mode === 'choix' && <div data-id="element-94" data-mp="29itxmdvdi51h">
                    <p className="text-[15px] text-slate-600" data-id="element-95" data-mp="24kipc7kq82ld">
                      Merci d’aider. Deux façons de retrouver son gardien.
                    </p>

                    <div className="mt-4 space-y-3" data-id="element-96" data-mp="1zjurp31zsni6">
                      <button type="button" onClick={() => setMode('scan')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-97" data-mp="1vkdjw2z3fe9u">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ville-indigo-soft text-ville-indigo" data-id="element-98" data-mp="2csd4apnwkv3p">
                          <QrCodeIcon className="h-5 w-5" aria-hidden="true" data-id="element-99" data-mp="1y4rz8q7gwlt5" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-100" data-mp="232jw7ymyyy47">
                          <span className="block text-[15px] font-bold text-ville-navy" data-id="element-101" data-mp="2qrl2pgc8rz6u">Scanner son collier</span>
                          <span className="block text-sm text-slate-500" data-id="element-102" data-mp="2vpmq2dgpako4">
                            L’animal porte une médaille avec un code
                          </span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-103" data-mp="2lr6vu1mdmvae" />
                      </button>

                      <button type="button" onClick={() => setMode('signalement')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-104" data-mp="16edqt9i841ov">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" data-id="element-105" data-mp="17tg81ial30s9">
                          <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-106" data-mp="k4njw807dx87" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-107" data-mp="f5u4ub59py5x">
                          <span className="block text-[15px] font-bold text-ville-navy" data-id="element-108" data-mp="tt5f89pprc4g">Signaler à la Ville</span>
                          <span className="block text-sm text-slate-500" data-id="element-109" data-mp="p25hx3coms1f">Aucune médaille ou code illisible</span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-110" data-mp="1bjjm3shcm4m" />
                      </button>
                    </div>
                  </div>}

                {mode === 'scan' && <div data-id="element-111" data-mp="q3ikhtqf6wox">
                    <div className="relative grid h-44 place-items-center overflow-hidden rounded-2xl bg-slate-100" data-id="element-112" data-mp="q2z0m7h7ly89">
                      <ScanLineIcon className="h-12 w-12 text-slate-400" aria-hidden="true" data-id="element-113" data-mp="uvs4yu8rn4jt" />
                      <motion.span className="absolute inset-x-8 h-0.5 bg-ville-indigo" initial={{
                  top: '20%'
                }} animate={{
                  top: ['20%', '80%', '20%']
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-114" data-mp="zs8thpjvnkxf" />
                    </div>

                    <ol className="mt-5 space-y-3" data-id="element-115" data-mp="h2su55jxhyil">
                      {['Approchez doucement l’animal, sans le brusquer.', 'Repérez la médaille sur son collier.', 'Cadrez le code QR de la médaille dans la zone ci-dessus.'].map((etape, index) => <li key={etape} className="flex gap-3 text-[15px] text-slate-600" data-id="element-116" data-mp="3czqhszzc0ytc" data-mpi={index}>
                          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-xs font-bold text-ville-indigo" data-id="element-117" data-mp="381p3stgrjpoq">
                            {index + 1}
                          </span>
                          {etape}
                        </li>)}
                    </ol>

                    <button type="button" onClick={() => setMode('scan-resultat')} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-118" data-mp="2pap7f76xvdlf">
                      Simuler le scan de la médaille
                    </button>
                    <button type="button" onClick={() => setMode('signalement')} className="mt-3 w-full text-sm font-semibold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-119" data-mp="3ilghlge9bt38">
                      Le code est illisible — signaler à la Ville
                    </button>
                  </div>}

                {mode === 'scan-resultat' && <div className="text-center" data-id="element-120" data-mp="34ubcvzw18am1">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-121" data-mp="34v3839lpw6bs">
                      <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-122" data-mp="2uvm6p4di0elm" />
                    </span>
                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-123" data-mp="p2x9u356p9e7">Médaille reconnue</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-124" data-mp="t4is640hmfvs">
                      Cet animal est enregistré à Beaconsfield. Son gardien vient d’être avisé qu’il a été trouvé.
                      Vos coordonnées ne lui sont pas transmises : la Ville fait le lien.
                    </p>
                    <p className="mt-3 rounded-2xl bg-slate-50 px-5 py-3 text-sm text-slate-600" data-id="element-125" data-mp="2nd4pebzdrvi3">
                      Médaille BEA-•••21 · avis envoyé à l’instant
                    </p>
                    <button type="button" onClick={close} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-126" data-mp="2x9hj39uxe9cu">
                      Terminer
                    </button>
                  </div>}

                {mode === 'signalement' && <div data-id="element-127" data-mp="16zxpzk8whz4j">
                    <p className="text-[15px] text-slate-600" data-id="element-128" data-mp="21kvbsspgnbs7">
                      Une photo et un lieu suffisent. Votre signalement crée une requête au service concerné.
                    </p>

                    <button type="button" onClick={() => setPhoto(true)} className={`mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${photo ? 'border-ville-indigo bg-ville-indigo-soft text-ville-indigo' : 'border-dashed border-slate-300 text-ville-navy hover:border-ville-indigo'}`} data-id="element-129" data-mp="1sk5flnwafjdy">
                      {photo ? <CheckIcon className="h-5 w-5" data-id="element-130" data-mp="34157iieq3w7g" /> : <CameraIcon className="h-5 w-5" data-id="element-131" data-mp="341f26japqrjh" />}
                      {photo ? 'Photo jointe' : 'Prendre une photo de l’animal'}
                    </button>

                    <fieldset className="mt-5" data-id="element-132" data-mp="36ll4kmjoi9xx">
                      <legend className="text-sm font-bold text-ville-navy" data-id="element-133" data-mp="3biuzghpu7dt5">Où l’avez-vous trouvé ?</legend>
                      <div className="mt-2 space-y-2" data-id="element-134" data-mp="3ghoe4dgv1auw">
                        {locationOptions.map(option => {
                    const selected = location === option.id;
                    return <label key={option.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-150 ease-ville focus-within:ring-2 focus-within:ring-ville-indigo ${selected ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-135" data-mp="3phur7y8nc8tj">
                              <input type="radio" name="lieu-trouve" value={option.id} checked={selected} onChange={() => setLocation(option.id)} className="sr-only" data-id="element-136" data-mp="h2azx4rjd0h4" />
                              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${selected ? 'bg-white text-ville-indigo' : 'bg-slate-100 text-ville-navy'}`} aria-hidden="true" data-id="element-137" data-mp="2i4sc8zlyidr3">
                                <option.Icon className="h-4 w-4" data-id="element-138" data-mp="35ssb6mtf9agr" />
                              </span>
                              <span className="min-w-0 flex-1" data-id="element-139" data-mp="30tyygwlylnhn">
                                <span className="block text-[15px] font-bold text-ville-navy" data-id="element-140" data-mp="1bhn2h43ha7uk">{option.label}</span>
                                {option.detail && <span className="block text-sm text-slate-500" data-id="element-141" data-mp="16k52i37fv2yp">{option.detail}</span>}
                              </span>
                              {selected && <CheckIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-142" data-mp="3n35omkq2f9lv" />}
                            </label>;
                  })}
                      </div>
                      {location === 'autre' && <div className="mt-3" data-id="element-143" data-mp="2zewbs8atxw6v">
                          <label htmlFor="lieu-trouve-adresse" className="block text-sm font-bold text-ville-navy" data-id="element-144" data-mp="2zd1owofzzu05">
                            Adresse ou intersection
                          </label>
                          <div className="mt-1.5" data-id="element-145" data-mp="2qfrawvbspqrj">
                            <AddressAutocomplete id="lieu-trouve-adresse" value={place} onChange={setPlace} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy focus:border-ville-indigo focus:outline-none focus:ring-2 focus:ring-ville-indigo/30" data-id="element-146" data-mp="2uf02pun9zekg" />
                          </div>
                        </div>}
                    </fieldset>

                    <button type="button" onClick={() => setMode('transmis')} disabled={!photo || location === 'autre' && !place.trim()} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:translate-y-0" data-id="element-147" data-mp="3q2w5srxhxl4v">
                      Envoyer à la Ville
                    </button>
                  </div>}

                {mode === 'transmis' && <div className="text-center" data-id="element-148" data-mp="18wpbi9zc1rzc">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-149" data-mp="18vw1n052umcz">
                      <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-150" data-mp="1hx40pkff3r84" />
                    </span>
                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-151" data-mp="1mw6ug8py6b0s">
                      Signalement transmis
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-152" data-mp="pa12ktenliie">
                      Votre signalement a été transmis au service animalier. Vous pouvez en suivre l’avancement dans
                      Mes demandes.
                    </p>
                    <p className="mt-3 font-mono text-sm text-slate-500" data-id="element-153" data-mp="13xm7msv39rsy">#REQ-2903</p>
                    <Link to="/mes-demandes/req-2903" onClick={close} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-154" data-mp="6i89kyzl3xao">
                      Suivre ma demande
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-155" data-mp="87c6g5y6gcqf" />
                    </Link>
                  </div>}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}